import React, { Component, RefObject, SyntheticEvent, createRef } from 'react';
import {
    NOT_NUMBER_REGEXP,
    SofaFormControl,
    SofaFormControlDescriptionWithGtm,
    SofaFormValidator,
    SofaFormWithGtm,
    SofaInput,
    getPropsForSofaInput,
    maxLengthFilter,
    minLengthValidator,
    setFocusToNextInputField,
    textFilter
} from '@sofology/sofologycomponents';
import getDaysInMonth from 'date-fns/getDaysInMonth';

import './SofaDateInput.scss';

import { analyticsHelper, errorToBasketErrorModel, messageArrayToHelperText } from 'helpers';


export const DAY_MIN_LENGTH_ERROR_MESSAGE = 'Day should be at least 1 digits';
export const MONTH_MIN_LENGTH_ERROR_MESSAGE = 'Month should be at least 1 digits';
export const YEAR_MIN_LENGTH_ERROR_MESSAGE = 'Year should be at least 4 digits';

export const DAY_VALIDATION_ERROR_MESSAGE = 'Please provide a valid day';
export const NO_SUCH_DAY_IN_MONTH_ERROR_MESSAGE = 'Please provide a valid day (no such day in the specified month)';
export const MONTH_VALIDATION_ERROR_MESSAGE = 'Please provide a valid month';
export const YEAR_VALIDATION_ERROR_MESSAGE = 'Please provide a valid year';

export const DAY_LENGTH = 2;
export const DAY_MIN_LENGTH = 1;
export const MONTH_LENGTH = 2;
export const MONTH_MIN_LENGTH = 1;
export const YEAR_LENGTH = 4;
export const YEAR_MIN_LENGTH = 4;

export const MIN_DAY_IN_MONTH = 1;
export const MAX_DAYS_IN_MONTH = 31;
export const MIN_MONTH_IN_YEAR = 1;
export const MAX_MONTH_IN_YEAR = 12;
export const MIN_YEAR = 1900;

const defaultMonthsDaysCount = {
    1: 31,
    2: 29,
    3: 31,
    4: 30,
    5: 31,
    6: 30,
    7: 31,
    8: 31,
    9: 30,
    10: 31,
    11: 30,
    12: 31
};

const DAYS_EXIST_NOT_IN_EVERY_MONTH = {
    29: 29,
    30: 30,
    31: 31
};

interface OwnProps {
    label: string;
    parentControl: any;
    description: string | null;
    name: string;
    onChange: (e: any) => void;
    onBlur: (e: SyntheticEvent) => void;
    value: any;
    helperText: string | null;
    addErrorClass: boolean;
    ref: RefObject<any> | undefined;
    caretPosition: number | undefined;
    helperTextType?: 'error' | 'info';
}

interface OwnState {
    dateForm: SofaFormWithGtm<DateControls>;
    originalParentControlValidateFunc: () => Promise<SofaFormControl<any>>;
}

interface DateControls {
    day: SofaFormControl<string>;
    month: SofaFormControl<string>;
    year: SofaFormControl<string>;
}

export class SofaDateInput extends Component<OwnProps, OwnState> {
    private element = React.createRef<HTMLDivElement>();
    private inputElement = React.createRef<HTMLInputElement>();

    constructor(props: OwnProps) {
        super(props);

        let day = '';
        let month = '';
        let year = '';

        if (props.value !== '' && props.value !== null) {
            const date = new Date(props.value);

            day = date.getDate().toString();
            month = (date.getMonth() + 1).toString();
            year = date.getFullYear().toString();
        }

        this.state = {
            dateForm: new SofaFormWithGtm<DateControls>(
                {
                    day: {
                        name: 'day',
                        id: 'dateField1',
                        defaultValue: day,
                        filters: [
                            textFilter(NOT_NUMBER_REGEXP),
                            maxLengthFilter(DAY_LENGTH)
                        ],
                        validators: [
                            minLengthValidator(DAY_MIN_LENGTH_ERROR_MESSAGE, DAY_MIN_LENGTH),
                            this.dayValidator(DAY_VALIDATION_ERROR_MESSAGE),
                            this.dayByMonthAndYearValidator(NO_SUCH_DAY_IN_MONTH_ERROR_MESSAGE),
                        ],
                        ref: createRef<SofaInput>(),
                        gtmFieldName: 'day',
                        onMaxLength: this.setFocusToNextField
                    } as SofaFormControlDescriptionWithGtm<string>,
                    month: {
                        name: 'month',
                        id: 'dateField2',
                        defaultValue: month,
                        filters: [
                            textFilter(NOT_NUMBER_REGEXP),
                            maxLengthFilter(MONTH_LENGTH)
                        ],
                        validators: [
                            minLengthValidator(MONTH_MIN_LENGTH_ERROR_MESSAGE, MONTH_MIN_LENGTH),
                            this.monthValidator(MONTH_VALIDATION_ERROR_MESSAGE),
                            this.monthByDayAndYearValidator(NO_SUCH_DAY_IN_MONTH_ERROR_MESSAGE),
                        ],
                        ref: createRef<SofaInput>(),
                        gtmFieldName: 'month',
                        onMaxLength: this.setFocusToNextField
                    } as SofaFormControlDescriptionWithGtm<string>,
                    year: {
                        name: 'year',
                        id: 'dateField3',
                        defaultValue: year,
                        filters: [
                            textFilter(NOT_NUMBER_REGEXP),
                            maxLengthFilter(YEAR_LENGTH)
                        ],
                        validators: [
                            minLengthValidator(YEAR_MIN_LENGTH_ERROR_MESSAGE, YEAR_MIN_LENGTH),
                            this.yearValidator(YEAR_VALIDATION_ERROR_MESSAGE),
                            this.yearByMonthAndDayValidator()
                        ],
                        ref: createRef<SofaInput>(),
                        gtmFieldName: 'year'
                    } as SofaFormControlDescriptionWithGtm<string>,
                },
                updateStateFunc => {
                    this.setState(updateStateFunc);
                },
                'dateForm',
                () => this.state,
                '',
                analyticsHelper.sendToGoogleMessageAboutInputChangeCompleted.bind(analyticsHelper),
                errorToBasketErrorModel
            ),
            originalParentControlValidateFunc: this.props.parentControl.validate
        };

        this.mountParentControlValidateFunc();
    }

    componentWillUnmount(): void {
        this.unmountParentControlValidateFunc();
    }

    componentDidUpdate(prevProps: Readonly<OwnProps>, prevState: Readonly<OwnState>) {
        if (this.props.caretPosition !== undefined) {
            this.inputElement.current?.setSelectionRange(this.props.caretPosition, this.props.caretPosition);
        }

        if (prevState.dateForm.controls.day.value !== this.state.dateForm.controls.day.value
            && this.state.dateForm.controls.month.isBlurHandled && !this.state.dateForm.controls.year.isBlurHandled) {
            this.state.dateForm.controls.month.validate();
        }

        if (prevState.dateForm.controls.day.value !== this.state.dateForm.controls.day.value
            && this.state.dateForm.controls.month.isBlurHandled && this.state.dateForm.controls.year.isBlurHandled) {
            this.state.dateForm.controls.month.validate();
            this.state.dateForm.controls.year.validate();
        }

        if (prevState.dateForm.controls.month.value !== this.state.dateForm.controls.month.value
            && this.state.dateForm.controls.day.isBlurHandled && !this.state.dateForm.controls.year.isBlurHandled) {
            this.state.dateForm.controls.day.validate();
        }

        if (prevState.dateForm.controls.month.value !== this.state.dateForm.controls.month.value
            && this.state.dateForm.controls.day.isBlurHandled && this.state.dateForm.controls.year.isBlurHandled) {
            this.state.dateForm.controls.day.validate();
            this.state.dateForm.controls.year.validate();
        }

        if (prevState.dateForm.controls.year.value !== this.state.dateForm.controls.year.value
            && this.state.dateForm.controls.day.isBlurHandled && this.state.dateForm.controls.month.isBlurHandled) {
            this.state.dateForm.controls.day.validate();
            this.state.dateForm.controls.month.validate();
        }

        if ((prevState.dateForm.controls.day.value !== this.state.dateForm.controls.day.value)
            || (prevState.dateForm.controls.month.value !== this.state.dateForm.controls.month.value)
            || (prevState.dateForm.controls.year.value !== this.state.dateForm.controls.year.value)) {
            this.props.parentControl.onChange(this.getFormData());
        }

        if (prevState.dateForm.controls.year.isBlurHandled !== this.state.dateForm.controls.year.isBlurHandled) {
            this.props.parentControl.onBlur();
        }
    }

    render(): JSX.Element {
        const { helperTextType, helperText, label, description } = this.props;
        const { day, month, year } = this.state.dateForm.controls;

        const dayInputHelperText = messageArrayToHelperText([day.errorMessage, month.errorMessage, year.errorMessage, helperText]);
        const errorClass = Boolean(day.errorMessage || month.errorMessage || year.errorMessage || helperText);

        let helperTextStyleModifier = '';

        switch (helperTextType) {
            case 'info':
                helperTextStyleModifier = 'sofa-input__helper-text--info';
                break;
            default:
                helperTextStyleModifier = 'sofa-input__helper-text--error';
                break;
        }

        return (
            <div className={`sofa-date-input ${helperTextStyleModifier}`} ref={this.element}>
                <div className='sofa-date-input__date-header'>
                    <div className='sofa-date-input__date-header__title'>{label} *</div>
                    <div className='sofa-date-input__date-header__description'>{description}</div>
                </div>
                <div className='sofa-date-input__date-content'>
                    <SofaInput
                        label='DD'
                        inputProps={{ min: 0, style: { textAlign: 'center' }, inputMode: 'decimal', pattern: '[0-9]*', autoCorrect: 'off', autoComplete: 'bday-day', maxLength: '2' }}
                        {...getPropsForSofaInput(day)}
                        helperText={dayInputHelperText}
                        addErrorClass={errorClass}
                    />
                    <SofaInput
                        label='MM'
                        inputProps={{ min: 0, style: { textAlign: 'center' }, inputMode: 'decimal', pattern: '[0-9]*', autoCorrect: 'off', autoComplete: 'bday-month', maxLength: '2' }}
                        {...getPropsForSofaInput(month)}
                        addErrorClass={errorClass}
                        helperText={null}
                    />
                    <SofaInput
                        label='YYYY'
                        inputProps={{ min: 0, style: { textAlign: 'center' }, inputMode: 'decimal', pattern: '[0-9]*', autoCorrect: 'off', autoComplete: 'bday-year', maxLength: '4' }}
                        {...getPropsForSofaInput(year)}
                        addErrorClass={errorClass}
                        helperText={null}
                    />
                </div>
            </div>
        );
    }

    public setFocus(): void {
        this.inputElement.current?.focus();
        this.inputElement.current?.setSelectionRange(0, 0);
    }

    setFocusToNextField = (control: SofaFormControl<string>) => {
        setFocusToNextInputField(control, this.state.dateForm.controls);
    };

    dayValidator = (errorMessage: string): SofaFormValidator<string> => {
        return (value: string) => {
            const day = Number(value);

            return Promise.resolve(
                day > MAX_DAYS_IN_MONTH || day < MIN_DAY_IN_MONTH
                    ? errorMessage
                    : null
            );
        };
    };

    monthValidator = (errorMessage: string): SofaFormValidator<string> => {
        return (value: string) => {
            const month = Number(value);

            return Promise.resolve(
                month > MAX_MONTH_IN_YEAR || month < MIN_MONTH_IN_YEAR
                    ? errorMessage
                    : null
            );
        };
    };

    yearValidator = (errorMessage: string): SofaFormValidator<string> => {
        return (value: string) => {
            const year = Number(value);

            return Promise.resolve(
                year < MIN_YEAR
                    ? errorMessage
                    : null
            );
        };
    };

    monthByDayAndYearValidator = (errorMessage: string): SofaFormValidator<string> => {
        return (value: string) => {
            const month = Number(value);
            const dayControl = this.state.dateForm.controls.day;
            const day = Number(dayControl.value);

            if (dayControl.value.length === DAY_LENGTH
                && dayControl.errorMessage === null) {
                const yearValue = this.state.dateForm.controls.year.value;
                const year = Number(yearValue);

                if (yearValue) {
                    if (yearValue.length < YEAR_LENGTH) {
                        return Promise.resolve(null);
                    }

                    return this.validateFullDate(day, month, year);
                }

                return Promise.resolve(
                    day > defaultMonthsDaysCount[month] && (day in DAYS_EXIST_NOT_IN_EVERY_MONTH)
                        ? errorMessage
                        : null
                );
            }

            return Promise.resolve(null);
        };
    };

    dayByMonthAndYearValidator = (errorMessage: string): SofaFormValidator<string> => {
        return (value: string) => {
            const day = Number(value);
            const monthControl = this.state.dateForm.controls.month;
            const month = Number(monthControl.value);

            if (monthControl.value.length === MONTH_LENGTH
                && monthControl.errorMessage === null) {
                const yearValue = this.state.dateForm.controls.year.value;
                const year = Number(yearValue);

                if (yearValue) {
                    if (yearValue.length < YEAR_LENGTH) {
                        return Promise.resolve(null);
                    }

                    return this.validateFullDate(day, month, year);
                }

                return Promise.resolve(
                    day > defaultMonthsDaysCount[month] && (day in DAYS_EXIST_NOT_IN_EVERY_MONTH)
                        ? errorMessage
                        : null
                );
            }

            return Promise.resolve(null);
        };
    };

    yearByMonthAndDayValidator = (): SofaFormValidator<string> => {
        return (value: string) => {
            const year = Number(value);
            const day = Number(this.state.dateForm.controls.day.value);
            const month = Number(this.state.dateForm.controls.month.value);

            if (this.state.dateForm.controls.day.value.length === DAY_LENGTH
                && this.state.dateForm.controls.day.value.length === MONTH_LENGTH) {
                return this.validateFullDate(day, month, year);
            }

            return Promise.resolve(null);
        };
    };

    validateFullDate = (day: number, month: number, year: number): Promise<string | null> => {
        const enteredDate = new Date(year, month - 1);

        return Promise.resolve(
            day > getDaysInMonth(enteredDate) && (day in DAYS_EXIST_NOT_IN_EVERY_MONTH)
                ? NO_SUCH_DAY_IN_MONTH_ERROR_MESSAGE : null
        );
    };

    private mountParentControlValidateFunc() {
        const originalParentControlValidateFunc = this.props.parentControl.validate;
        this.props.parentControl.validate = () => {
            return this.state.dateForm.validate().then(invalidControls => {
                if (invalidControls.length === 0) {
                    return originalParentControlValidateFunc();
                }

                return this.props.parentControl;
            });
        };
    }

    private unmountParentControlValidateFunc() {
        this.props.parentControl.validate = this.state.originalParentControlValidateFunc;
    }

    getFormData(): Date | null {
        let day = Number(this.state.dateForm.controls.day.value);
        let month = Number(this.state.dateForm.controls.month.value);
        let year = Number(this.state.dateForm.controls.year.value);

        if (day === 0 || month === 0 || year === 0 || year < MIN_YEAR) {
            return null;
        }

        const date = new Date(year, month - 1, day);

        return date;
    }
}